/* Mayfly Studios — 360 viewer + extra animation layer.
 * Loads after mf-animations.js. Same contract: everything routes through
 * window.mfGSAP() so prefers-reduced-motion is honoured and contexts revert.
 */
(function () {
  if (typeof window === 'undefined') return;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    if (!window.gsap || !window.mfGSAP) return;

    var qs = function (s, c) { return (c || document).querySelector(s); };
    var qsa = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var reduced = !!window.mfReducedMotion;
    var contexts = (window.mfAnimations && window.mfAnimations.contexts) || [];
    function track(c) { if (c) contexts.push(c); return c; }

    /* ==============================================================
     * 360 PRODUCT VIEWER
     *
     * MODE A — true 360. Active when the product supplies a frame sequence via
     * <script type="application/json" data-mf-360-frames> containing an ordered
     * array of 24-36 image URLs. Drag scrubs frames: a real garment rotation.
     *
     * MODE B — fallback for single-photo products (all of them today). Drag
     * rotates the photo on the Y axis with perspective, clamped to ±26° and
     * spring-returning on release. We deliberately do NOT allow a full 360
     * here: past ~40° a flat photo turns edge-on then mirrors, so the print
     * reads backwards.
     * ============================================================== */
    function initViewer(photo, wrap) {
      var framesEl = qs('[data-mf-360-frames]');
      var frames = null;
      if (framesEl) {
        try {
          var parsed = JSON.parse(framesEl.textContent);
          if (Array.isArray(parsed) && parsed.length >= 8) frames = parsed;
        } catch (e) { /* malformed list — fall through to Mode B */ }
      }
      if (frames) return initTrue360(photo, wrap, frames);
      return initBoundedRotate(photo, wrap);
    }

    function initTrue360(photo, wrap, frames) {
      return window.mfGSAP(wrap, function (o) {
        frames.forEach(function (src) { var i = new Image(); i.src = src; });
        var idx = 0, dragging = false, lastX = 0, accum = 0;
        var PX_PER_FRAME = 12;

        function show(i) {
          idx = ((i % frames.length) + frames.length) % frames.length;
          photo.src = frames[idx];
        }
        show(0);
        if (o.reduced) return;

        var spin = null;
        function down(e) {
          dragging = true; lastX = e.clientX; accum = 0;
          if (spin) { spin.kill(); spin = null; }
          wrap.style.cursor = 'grabbing';
          if (wrap.setPointerCapture) { try { wrap.setPointerCapture(e.pointerId); } catch (err) {} }
          e.preventDefault();
        }
        function move(e) {
          if (!dragging) return;
          accum += (e.clientX - lastX);
          lastX = e.clientX;
          var step = Math.trunc(accum / PX_PER_FRAME);
          if (step) { show(idx + step); accum -= step * PX_PER_FRAME; }
        }
        function up(e) {
          if (!dragging) return;
          dragging = false;
          wrap.style.cursor = 'grab';
          var v = (e && e.movementX) || 0;
          if (Math.abs(v) < 2) return;
          var target = { f: idx };
          spin = gsap.to(target, {
            f: idx + v * 1.6, duration: 1.1, ease: 'power3.out',
            onUpdate: function () { show(Math.round(target.f)); }
          });
        }

        wrap.style.cursor = 'grab';
        wrap.style.touchAction = 'pan-y';
        wrap.addEventListener('pointerdown', down);
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        addHint(wrap, 'Drag to rotate');

        return function () {
          wrap.removeEventListener('pointerdown', down);
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          if (spin) spin.kill();
        };
      });
    }

    function initBoundedRotate(photo, wrap) {
      return window.mfGSAP(wrap, function (o) {
        if (o.reduced) { gsap.set(photo, { rotationY: 0, rotationX: 0 }); return; }

        var LIMIT = 26;
        gsap.set(wrap, { perspective: 1000 });
        gsap.set(photo, { transformOrigin: '50% 50%' });
        var ry = gsap.quickTo(photo, 'rotationY', { duration: 0.4, ease: 'power2.out' });
        var rx = gsap.quickTo(photo, 'rotationX', { duration: 0.4, ease: 'power2.out' });
        var dragging = false, startX = 0, startY = 0;

        function down(e) {
          dragging = true; startX = e.clientX; startY = e.clientY;
          wrap.style.cursor = 'grabbing';
          if (wrap.setPointerCapture) { try { wrap.setPointerCapture(e.pointerId); } catch (err) {} }
          e.preventDefault();
        }
        function move(e) {
          if (!dragging) return;
          ry(Math.max(-LIMIT, Math.min(LIMIT, (e.clientX - startX) * 0.35)));
          rx(Math.max(-LIMIT / 2, Math.min(LIMIT / 2, -(e.clientY - startY) * 0.2)));
        }
        function up() {
          if (!dragging) return;
          dragging = false;
          wrap.style.cursor = 'grab';
          gsap.to(photo, { rotationY: 0, rotationX: 0, duration: 0.9, ease: 'elastic.out(1, 0.55)' });
        }

        wrap.style.cursor = 'grab';
        wrap.style.touchAction = 'pan-y';
        wrap.addEventListener('pointerdown', down);
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        addHint(wrap, 'Drag to turn');

        return function () {
          wrap.removeEventListener('pointerdown', down);
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
      });
    }

    function addHint(wrap, text) {
      if (qs('[data-mf-360-hint]', wrap)) return;
      var hint = document.createElement('span');
      hint.setAttribute('data-mf-360-hint', '');
      hint.textContent = text;
      hint.style.cssText = 'position:absolute;left:50%;bottom:12px;transform:translateX(-50%);' +
        'font-size:9px;font-weight:500;letter-spacing:0.18em;text-transform:uppercase;color:#333333;' +
        'background:rgba(255,255,255,0.85);padding:5px 10px;border-radius:9999px;pointer-events:none;z-index:2';
      if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      wrap.appendChild(hint);
      gsap.to(hint, { opacity: 0, delay: 3.5, duration: 0.6 });
    }

    var pdpPhoto = qs('[data-mf-pdp-photo]');
    if (pdpPhoto && pdpPhoto.parentElement) {
      track(initViewer(pdpPhoto, pdpPhoto.parentElement));
    }

    /* ============ EXTRA ANIMATIONS ============ */

    /* 1. Magnetic CTAs */
    if (canHover && !reduced) {
      qsa('.mf-hover-invert, [data-mf-pdp-add], [data-mf-magnetic]').forEach(function (btn) {
        track(window.mfGSAP(btn, function (o) {
          if (o.reduced) return;
          var mx = gsap.quickTo(btn, 'x', { duration: 0.45, ease: 'power3.out' });
          var my = gsap.quickTo(btn, 'y', { duration: 0.45, ease: 'power3.out' });
          function onMove(e) {
            var r = btn.getBoundingClientRect();
            mx((e.clientX - (r.left + r.width / 2)) * 0.22);
            my((e.clientY - (r.top + r.height / 2)) * 0.35);
          }
          function onLeave() { mx(0); my(0); }
          btn.addEventListener('mousemove', onMove);
          btn.addEventListener('mouseleave', onLeave);
          return function () {
            btn.removeEventListener('mousemove', onMove);
            btn.removeEventListener('mouseleave', onLeave);
          };
        }));
      });
    }

    /* 2. Cart subtotal count-up */
    var subtotal = qs('[data-mf-subtotal]');
    if (subtotal && window.MutationObserver && !reduced) {
      track(window.mfGSAP(subtotal, function (o) {
        if (o.reduced) return;
        var prev = parseFloat((subtotal.textContent || '').replace(/[^\d.]/g, '')) || 0;
        var busy = false;
        var obs = new MutationObserver(function () {
          if (busy) return;
          var next = parseFloat((subtotal.textContent || '').replace(/[^\d.]/g, '')) || 0;
          if (next === prev) return;
          var symbol = (subtotal.textContent || '').replace(/[\d.,]/g, '').trim();
          var o2 = { n: prev };
          busy = true;
          gsap.to(o2, {
            n: next, duration: 0.55, ease: 'power2.out',
            onUpdate: function () { subtotal.textContent = symbol + Math.round(o2.n).toLocaleString('en-IN'); },
            onComplete: function () { prev = next; busy = false; }
          });
        });
        obs.observe(subtotal, { childList: true, characterData: true, subtree: true });
        return function () { obs.disconnect(); };
      }));
    }

    /* 3. Line-by-line heading reveal — hand-rolled, no SplitText dependency */
    function splitLines(el) {
      var text = el.textContent;
      if (!text || !text.trim()) return null;
      el.textContent = '';
      var words = text.trim().split(/\s+/);
      var spans = words.map(function (w, i) {
        var s = document.createElement('span');
        s.textContent = w + (i < words.length - 1 ? ' ' : '');
        s.style.display = 'inline-block';
        el.appendChild(s);
        return s;
      });
      var lines = [], currentTop = null, bucket = null;
      spans.forEach(function (s) {
        var top = Math.round(s.offsetTop);
        if (currentTop === null || Math.abs(top - currentTop) > 2) {
          currentTop = top; bucket = []; lines.push(bucket);
        }
        bucket.push(s);
      });
      el.textContent = '';
      return lines.map(function (group) {
        var mask = document.createElement('span');
        mask.style.cssText = 'display:block;overflow:hidden';
        var inner = document.createElement('span');
        inner.style.cssText = 'display:block';
        group.forEach(function (s) { inner.appendChild(s); });
        mask.appendChild(inner);
        el.appendChild(mask);
        return inner;
      });
    }

    if (window.ScrollTrigger) {
      qsa('[data-mf-split], #basics h2, #standard h2').forEach(function (el) {
        track(window.mfGSAP(el, function (o) {
          var lines = splitLines(el);
          if (!lines || !lines.length) return;
          if (o.reduced) { gsap.set(lines, { yPercent: 0, opacity: 1 }); return; }
          ScrollTrigger.create({
            trigger: el, start: 'top 88%', once: true,
            onEnter: function () {
              gsap.fromTo(lines, { yPercent: 105 },
                { yPercent: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08 });
            }
          });
        }));
      });
    }

    /* 4. Footer link stagger */
    var footer = qs('footer');
    if (footer && window.ScrollTrigger) {
      track(window.mfGSAP(footer, function (o) {
        var links = qsa('.mf-footer-link', footer);
        if (!links.length) return;
        if (o.reduced) { gsap.set(links, { opacity: 1, y: 0 }); return; }
        ScrollTrigger.create({
          trigger: footer, start: 'top 90%', once: true,
          onEnter: function () {
            gsap.fromTo(links, { opacity: 0, y: 10 },
              { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.03, clearProps: 'transform' });
          }
        });
      }));
    }

    console.log('[mf-extra] 360 mode: ' + (qs('[data-mf-360-frames]') ? 'frames' : 'bounded-fallback') +
      ' | contexts now ' + contexts.length);
  });
})();
